import { useEffect, useState } from 'react';
import { EventBus, sessionTitle, useGameStore, useLocalBubbleId, useRecordingBubbles } from '@campus/core';
import type { LiveSessionDTO } from '@campus/core';
import { API_BASE } from '../../lib/apiBase.js';
import { Button, Icon } from '../ds/index.js';
import { apiHeaders } from '../people/membersStore.js';
import { useElapsed } from '../hooks/useElapsed.js';

const POLL_MS = 20_000;

/**
 * LiveSessions — « Coaching en cours - Ascension Ads » : un bandeau par salle
 * de réunion qui enregistre, vu seulement par ceux qui peuvent y entrer (le
 * serveur filtre, GET /media/recordings/live). « Rejoindre » y mène à pied,
 * comme pour rejoindre quelqu'un. Caché dans la salle elle-même ; la croix le
 * cache pour cette séance seulement.
 * Liste relue toutes les 20 s, et tout de suite quand un enregistrement
 * démarre ou s'arrête sur la carte courante.
 */
export function LiveSessions() {
  const authState = useGameStore((s) => s.authState);
  const localBubbleId = useLocalBubbleId();
  const recordingBubbles = useRecordingBubbles();
  const [sessions, setSessions] = useState<LiveSessionDTO[]>([]);
  const [hidden, setHidden] = useState<Set<string>>(() => new Set());
  const inGame = authState === 'in-game';

  useEffect(() => {
    if (!inGame) { setSessions([]); return; }
    let cancelled = false;
    const load = async () => {
      try {
        const res = await fetch(`${API_BASE}/media/recordings/live`, { headers: apiHeaders() });
        if (!res.ok || cancelled) return;
        const list = await res.json() as LiveSessionDTO[];
        if (!cancelled) setSessions(Array.isArray(list) ? list : []);
      } catch { /* réseau : on garde la dernière liste */ }
    };
    void load();
    const timer = setInterval(() => { void load(); }, POLL_MS);
    return () => { cancelled = true; clearInterval(timer); };
  }, [inGame, recordingBubbles]);

  const visible = sessions.filter((s) => s.roomId !== localBubbleId && !hidden.has(s.recordingId));
  if (!inGame || visible.length === 0) return null;
  return (
    <>
      {visible.map((s) => (
        <LiveSessionCard key={s.recordingId} session={s} onHide={() => setHidden((h) => new Set(h).add(s.recordingId))} />
      ))}
    </>
  );
}

function LiveSessionCard({ session, onHide }: { session: LiveSessionDTO; onHide: () => void }) {
  const elapsed = useElapsed(Date.parse(session.startedAt));
  const here = useGameStore((s) => s.currentZoneId?.split(':')[1] === session.zoneSlug);
  const join = () => {
    EventBus.emit('ui:go-to-place', { zoneSlug: session.zoneSlug, x: session.x, y: session.y, name: session.roomName });
    EventBus.emit('ui:toast', { message: `En route vers ${session.roomName}.`, tone: 'info' });
  };
  return (
    <div className="pl-pop pl-live-session" role="status">
      <button type="button" className="pl-live-session-main" onClick={join} title={`Rejoindre ${session.roomName}`}>
        <span className="pl-live-session-dot" aria-hidden="true" />
        <span className="pl-live-session-text">
          <span className="pl-live-session-title">{sessionTitle(session.sessionType)} - {session.roomName}</span>
          <span className="pl-live-session-sub">
            {here ? 'Sur cette carte' : session.zoneName}{elapsed ? ` · depuis ${elapsed}` : ''}
          </span>
        </span>
      </button>
      <Button variant="primary" size="sm" onClick={join}>Rejoindre</Button>
      <button type="button" className="pl-toast-close" onClick={onHide} aria-label="Masquer">
        <Icon name="x" size={14} />
      </button>
    </div>
  );
}
